/**
 * Tronque une chaîne (comme un hash) en insérant des points de suspension au milieu
 * @param {string} str - La chaîne à tronquer
 * @param {number} startChars - Nombre de caractères à conserver au début
 * @param {number} endChars - Nombre de caractères à conserver à la fin
 * @param {string} separator - Le séparateur à utiliser (par défaut : "...")
 * @returns {string} La chaîne tronquée
 */
export function truncateHash(str, startChars = 6, endChars = 6, separator = "...") {
    // Si la chaîne est vide ou non définie, retourner une chaîne vide
    if (!str) return ""
  
    // Si la chaîne est plus courte que la somme des caractères à conserver, la retourner entière
    if (str.length <= startChars + endChars) {
      return str
    }
  
    // Sinon, prendre les premiers startChars, ajouter le séparateur, puis les derniers endChars
    return `${str.substring(0, startChars)}${separator}${str.substring(str.length - endChars)}`
  }
  
  /**
   * Version responsive de la fonction truncateHash qui adapte la longueur en fonction de la taille de l'écran
   * @param {string} str - La chaîne à tronquer
   * @param {Object} options - Options de configuration pour différentes tailles d'écran
   * @returns {string} La chaîne tronquée
   */
  export function responsiveTruncateHash(str, options = {}) {
    // Valeurs par défaut pour différentes tailles d'écran
    const defaultOptions = {
      xs: { start: 4, end: 4 }, // Écrans très petits
      sm: { start: 5, end: 5 }, // Écrans petits
      md: { start: 6, end: 6 }, // Écrans moyens
      lg: { start: 8, end: 8 }, // Écrans grands
      xl: { start: 10, end: 10 }, // Écrans très grands
    }
  
    // Fusionner les options par défaut avec celles fournies
    const config = { ...defaultOptions, ...options }
  
    // Déterminer la taille de l'écran
    let screenSize = "md" // Taille par défaut
  
    if (typeof window !== "undefined") {
      const width = window.innerWidth
      if (width < 640) screenSize = "xs"
      else if (width < 768) screenSize = "sm"
      else if (width < 1024) screenSize = "md"
      else if (width < 1280) screenSize = "lg"
      else screenSize = "xl"
    }
  
    // Appliquer la troncature avec les paramètres appropriés pour la taille de l'écran
    const { start, end } = config[screenSize]
    return truncateHash(str, start, end)
  }
  
  /**
   * Détermine si un élément est visible dans la zone d'affichage
   * @param {HTMLElement} element - L'élément à vérifier
   * @returns {boolean} True si l'élément est visible
   */
  export function isElementInViewport(element) {
    if (!element) return false
  
    const rect = element.getBoundingClientRect()
  
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    )
  }
  
  /**
   * Calcule la longueur optimale pour la troncature en fonction de la largeur de l'élément
   * @param {HTMLElement} element - L'élément conteneur
   * @param {number} minChars - Nombre minimum de caractères à afficher (par côté)
   * @param {number} maxChars - Nombre maximum de caractères à afficher (par côté)
   * @returns {Object} Objet avec start et end caractères à afficher
   */
  export function calculateOptimalTruncation(element, minChars = 4, maxChars = 10) {
    if (!element) return { start: 6, end: 6 }
  
    const width = element.offsetWidth
  
    // Calculer une valeur entre minChars et maxChars en fonction de la largeur
   
    const charCount = Math.max(minChars, Math.min(maxChars, Math.floor(width / 10)))
  
    return { start: charCount, end: charCount }
  }
  